import React, { useEffect, useMemo, useRef, useState } from "react";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { Button } from "./ui/button";
import { Loader2, MapPin, X } from "lucide-react";

const CAT = {
  education: { color: "#8b5cf6", label: "School" },
  transit: { color: "#06b6d4", label: "Transit" },
  park: { color: "#22c55e", label: "Park" },
  shop: { color: "#f59e0b", label: "Shop" },
  other: { color: "#94a3b8", label: "Place" },
};

const keyOf = (p) => `${(p.name || "").toLowerCase()}|${(+p.lat).toFixed(5)}|${(+p.lng).toFixed(5)}`;

function pinIcon(color, filled) {
  return L.divIcon({
    className: "",
    html:
      `<div style="width:18px;height:18px;transform:translate(-50%,-100%);">` +
      `<svg viewBox="0 0 24 24" width="18" height="18" fill="${filled ? color : "transparent"}" stroke="${color}" stroke-width="2">` +
      `<path d="M12 2c-3.9 0-7 3.1-7 7 0 5 7 13 7 13s7-8 7-13c0-3.9-3.1-7-7-7z"/>` +
      `<circle cx="12" cy="9" r="2.2" fill="#fff" stroke="none" opacity="${filled ? 1 : 0}"/></svg></div>`,
    iconSize: [18, 18],
    iconAnchor: [9, 18],
  });
}

/**
 * Points-of-interest field. Fetches nearby POIs (schools, transit, parks, shops)
 * around the project location and shows them on a map — the client ticks the
 * relevant ones (click the pin) and adds their own by clicking the map. Stored
 * as a structured list so the public Spinhouse map can render them directly.
 */
export default function SetupPointsField({ q, api, taskId, currentValue, t, saving, onSavePoints }) {
  const mapRef = useRef(null);
  const mapObj = useRef(null);
  const layerRef = useRef(null);
  const [center, setCenter] = useState(null);
  const [needsAddress, setNeedsAddress] = useState(false);
  const [pois, setPois] = useState([]);
  const [loading, setLoading] = useState(true);
  // Selected = the points that will be saved, keyed for quick toggling.
  const [selected, setSelected] = useState(() => {
    const m = {};
    (Array.isArray(currentValue) ? currentValue : []).forEach((p) => { m[keyOf(p)] = p; });
    return m;
  });

  // Fetch suggestions once.
  useEffect(() => {
    let off = false;
    api.get(`/tasks/${taskId}/setup-chat/nearby-pois`)
      .then(({ data }) => {
        if (off) return;
        setCenter(data.center);
        setNeedsAddress(!!data.needs_address);
        setPois(data.pois || []);
      })
      .catch(() => { if (!off) setPois([]); })
      .finally(() => { if (!off) setLoading(false); });
    return () => { off = true; };
  }, [api, taskId]);

  const selectedList = useMemo(() => Object.values(selected), [selected]);

  const toggle = (p) => {
    setSelected((cur) => {
      const k = keyOf(p);
      const next = { ...cur };
      if (next[k]) delete next[k]; else next[k] = { name: p.name, lat: +p.lat, lng: +p.lng, category: p.category || "other" };
      return next;
    });
  };

  // Build the map once we have a centre.
  useEffect(() => {
    if (!center || !mapRef.current || mapObj.current) return;
    const map = L.map(mapRef.current, {
      center: [center.lat, center.lng], zoom: 15,
      zoomControl: true, attributionControl: false, scrollWheelZoom: false,
    });
    L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", { maxZoom: 19 }).addTo(map);
    // The project itself.
    L.marker([center.lat, center.lng], { icon: pinIcon("#e11d48", true) }).addTo(map);
    // Click empty map → add a custom point.
    map.on("click", (e) => {
      const name = window.prompt(t("pointsAddPrompt"));
      if (!name || !name.trim()) return;
      const p = { name: name.trim(), lat: e.latlng.lat, lng: e.latlng.lng, category: "other" };
      setSelected((cur) => ({ ...cur, [keyOf(p)]: p }));
    });
    layerRef.current = L.layerGroup().addTo(map);
    mapObj.current = map;
    setTimeout(() => map.invalidateSize(), 60);
    return () => { map.remove(); mapObj.current = null; };
  }, [center, t]);

  // Redraw suggestion + selected markers whenever either changes.
  useEffect(() => {
    if (!mapObj.current || !layerRef.current) return;
    const lg = layerRef.current;
    lg.clearLayers();
    const draw = (p, isSel) => {
      const c = (CAT[p.category] || CAT.other).color;
      const mk = L.marker([p.lat, p.lng], { icon: pinIcon(c, isSel) });
      mk.on("click", (ev) => { L.DomEvent.stopPropagation(ev); toggle(p); });
      mk.bindTooltip(p.name, { direction: "top" });
      mk.addTo(lg);
    };
    // Suggestions not yet selected (hollow), then selected (filled).
    pois.filter((p) => !selected[keyOf(p)]).forEach((p) => draw(p, false));
    selectedList.forEach((p) => draw(p, true));
  }, [pois, selected, selectedList]);

  const save = () => onSavePoints(selectedList.map((p) => ({ name: p.name, lat: p.lat, lng: p.lng, category: p.category })));

  if (loading) {
    return <div className="text-[11px] text-zinc-500 flex items-center gap-1.5"><Loader2 className="h-3 w-3 animate-spin" /> {t("pointsLoading")}</div>;
  }
  if (needsAddress) {
    return <div className="text-[11px] text-amber-300/90">{t("pointsNeedAddress")}</div>;
  }

  return (
    <div className="space-y-2">
      <div ref={mapRef} className="w-full h-[210px] rounded-lg overflow-hidden border border-zinc-700 z-0" />
      <div className="text-[10px] text-zinc-500 flex items-center gap-1">
        <MapPin className="h-3 w-3 text-cyan-400" />
        {t("pointsHint")}
      </div>
      {selectedList.length > 0 && (
        <ul className="space-y-0.5 max-h-28 overflow-y-auto">
          {selectedList.map((p) => {
            const c = (CAT[p.category] || CAT.other).color;
            return (
              <li key={keyOf(p)} className="flex items-center gap-1.5 text-[11px] text-zinc-300">
                <span className="h-2 w-2 rounded-full flex-shrink-0" style={{ background: c }} />
                <span className="flex-1 min-w-0 truncate">{p.name}</span>
                <button type="button" onClick={() => toggle(p)} className="text-zinc-500 hover:text-red-400 flex-shrink-0">
                  <X className="h-3 w-3" />
                </button>
              </li>
            );
          })}
        </ul>
      )}
      <div className="flex items-center gap-2">
        <Button size="sm" disabled={saving} onClick={save} className="h-7 px-3 bg-cyan-600 hover:bg-cyan-500 text-white text-xs">
          {saving ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : null}
          {t("pointsSave", selectedList.length)}
        </Button>
        <span className="text-[10px] text-zinc-500">{t("pointsSuggested", pois.length)}</span>
      </div>
    </div>
  );
}
